import { Link, Outlet, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Menu, X } from 'lucide-react'
import { Navigation } from '../components/customer/Navigation'

const navItems = [
  { to: '/admin', labelKey: 'nav.adminDashboard' },
  { to: '/admin/companies', labelKey: 'nav.adminCompanies' },
  { to: '/admin/registrations', labelKey: 'nav.adminRegistrations' },
  { to: '/admin/payments', labelKey: 'nav.adminPayments' },
  { to: '/admin/payouts', labelKey: 'nav.adminPayouts' },
  { to: '/admin/analytics', labelKey: 'nav.adminAnalytics' },
  { to: '/admin/users', labelKey: 'nav.adminUsers' },
  { to: '/admin/audit', labelKey: 'nav.adminAudit' },
  { to: '/admin/settings', labelKey: 'nav.adminSettings' },
]

/**
 * Admin panel layout (/admin/*).
 * Uses the same top navigation as the main homepage, then admin sidebar + content.
 * Sidebar is collapsible on mobile (hamburger opens drawer).
 */
export default function AdminLayout() {
  const { t } = useTranslation('portal')
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const isActive = (to: string) =>
    location.pathname === to || (to !== '/admin' && location.pathname.startsWith(to))
  const linkClass = (to: string) =>
    isActive(to) ? 'font-medium text-blue-600' : 'text-gray-600 hover:text-gray-900'

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <div className="flex flex-1 pt-16 relative">
        {/* Mobile backdrop when sidebar is open */}
        <button
          type="button"
          aria-hidden="true"
          tabIndex={-1}
          className={`md:hidden fixed inset-0 bg-black/40 z-10 transition-opacity ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={() => setSidebarOpen(false)}
        />
        {/* Admin sidebar: drawer on mobile, static on md+ */}
        <aside
          className={`
            w-52 border-r bg-white flex flex-col shrink-0
            fixed inset-y-0 left-0 pt-16 z-20 transform transition-transform duration-200 ease-out
            md:static md:pt-0 md:translate-x-0
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          <div className="p-4 flex items-center justify-between border-b md:border-b-0">
            <span className="font-medium text-gray-900">Admin</span>
            <button
              type="button"
              className="md:hidden p-2 -m-2 text-gray-500 hover:text-gray-900"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="p-4 flex flex-col gap-1 overflow-y-auto">
            {navItems.map(({ to, labelKey }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setSidebarOpen(false)}
                className={`block py-2 px-2 rounded-lg ${linkClass(to)}`}
              >
                {t(labelKey)}
              </Link>
            ))}
          </nav>
        </aside>
        {/* Main content + mobile menu button */}
        <main className="flex-1 min-w-0 flex flex-col bg-gray-50">
          <div className="flex items-center gap-2 p-3 border-b border-gray-200 bg-white md:hidden">
            <button
              type="button"
              className="p-2 -ml-2 text-gray-600 hover:text-gray-900"
              onClick={() => setSidebarOpen(true)}
              aria-label={t('nav.adminMenu')}
            >
              <Menu className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium text-gray-700">{t('nav.adminMenu')}</span>
          </div>
          <div className="flex-1 p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
